import "./index.scss";

import { Box, Button as MuiButton, ButtonGroup, Modal, Stack } from "@mui/material";
import { ReactComponent as IconUserIneligible } from "assets/images/icons/user-ineligible.svg";
import { ReactComponent as IconWorkflowStepActive } from "assets/images/icons/workflow-step-active.svg";
import { ReactComponent as IconWorkflowStepComplete } from "assets/images/icons/workflow-step-complete.svg";
import { ReactComponent as IconWorkflowStepLocked } from "assets/images/icons/workflow-step-locked.svg";
import { ReactComponent as DeleteIcon } from "assets/images/square-buttons/close-small.svg";
import { ReactComponent as IconYMCATCALogo } from "assets/images/tca/ymca-tca-logo.svg";
import Button from "components/Button";
import ConfirmationBox from "components/ConfirmationBox";
import ScreenLoader from "components/ScreenLoader";
import InitialTCASteps from "containers/CoachApproach/InitialTCASteps";
import MemberForm from "containers/Members/MemberForm";
import { TMember } from "models/member";
import { TTCAInitialSteps } from "models/tca";
import {
  addMemberTCAGoals,
  addMemberTCAPersonalFeelingsInventory,
  addMemberTCARemsScores,
  createMember,
  deleteTCAMemberAppointments,
} from "modules/member/memberServices";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

const AddMemberToCoachApproachModal = (props: {
  open: boolean;
  onClose: any;
  existingMember?: any;
}) => {
  const { open, onClose, existingMember } = props;
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [workflowState, setWorkflowState] = useState<"readiness" | "registration" | "tcainit">(
    "readiness"
  );
  const [readinessFirstQuestionAnswerIsYes, setReadinessFirstQuestionAnswerIsYes] = useState(false);
  const [readinessSecondQuestionAnswerIsYes, setReadinessSecondQuestionAnswerIsYes] =
    useState(false);
  const [memberToRegister, setMemberToRegister] = useState<TMember>(null);
  const [tcaInitialSteps, setTcaInitialSteps] = useState<TTCAInitialSteps>(null);
  const [showDataWillBeLostModal, setShowDataWillBeLostModal] = useState(false);
  const memberFormRef = useRef(null);
  const tcaInitialStepsFormRef = useRef(null);

  //reset the form once the modal is open
  useEffect(() => {
    setWorkflowState("readiness");
    setReadinessFirstQuestionAnswerIsYes(false);
    setReadinessSecondQuestionAnswerIsYes(false);
    //TODO: reset sub registration form & TCA Steps
  }, [open]);

  //#region event handlers
  const handleCloseModal = (isDiscardConfirmed) => {
    if (typeof isDiscardConfirmed === "boolean") {
      setShowDataWillBeLostModal(false);
      if (isDiscardConfirmed) {
        setMemberToRegister(null);
        setTcaInitialSteps(null);
        onClose();
      }
    } else {
      setShowDataWillBeLostModal(true);
    }
  };

  const handleSwitchMainWorkflowTab = (stage: string) => {
    if (stage === "readiness") {
      setWorkflowState("readiness");
    }
    if (stage === "registration") {
      if (!readinessFirstQuestionAnswerIsYes || !readinessSecondQuestionAnswerIsYes) {
        setWorkflowState("registration");
      }
    }
    if (stage === "tcainit") {
      //Make sure we passed the member registration (we have a member object)
      if (memberToRegister) {
        setWorkflowState("tcainit");
      }
    }
    //If memberToRegister already has a value, get an update just in case user is navigating from the tab at the top
    if (memberToRegister) {
      setMemberToRegister(memberFormRef.current.getUpdates());
    }
    //If tcaInitialSteps already has a value, get an update just in case user is navigating from the tab at the top
    if (tcaInitialSteps) {
      setTcaInitialSteps(tcaInitialStepsFormRef.current.getUpdates());
    }
  };

  const handleMemberObjectUpdated = (data, isReadyToSave: boolean = false) => {
    setMemberToRegister(data);
    if (isReadyToSave) {
      //Simply move forward to TCA initial steps
      setWorkflowState("tcainit");
    }
  };

  const setLoadingState = (state: boolean) => {
    setLoading(state);
  };

  const handleTCAObjectUpdated = async (
    data,
    isReadyToSave: boolean = false,
    goToWorkoutPage: boolean = false
  ) => {
    setTcaInitialSteps(data);
    if (isReadyToSave) {
      setLoading(true);
      if (existingMember) {
        handleAddingMemberToTCA(existingMember.id, true);
      } else {
        //console.log(`memberToRegister:\n`, memberToRegister);
        const memberPromise = new Promise(async function (resolve, reject) {
          const memberCreationResult = await createMember(
            memberToRegister,
            tcaInitialSteps,
            setLoadingState
          );
          console.log(
            `handleMemberObjectUpdated() memberCreationResult is ${JSON.stringify(
              memberCreationResult
            )}`
          );
          console.log(`handleMemberObjectUpdated() result is ${memberCreationResult["success"]}`);
          if (memberCreationResult["success"]) {
            const createdMember = memberCreationResult.data;
            handleAddingMemberToTCA(createdMember.lngMemberIDcnt, true, goToWorkoutPage);
            resolve(`memberPromise succeeded with result ${JSON.stringify(memberCreationResult)}`);
          } else {
            setLoading(false);
            reject(`memberPromise failed with result ${JSON.stringify(memberCreationResult)}`);
            return;
          }
        });
        await memberPromise;
      }
    }
  };

  const handleAddingMemberToTCA = async (
    memberId: number,
    initialAdd: boolean = false,
    goToWorkoutPage: boolean = false
  ) => {
    //Add REMS Scorings
    const remsPromise = new Promise(async function (resolve, reject) {
      const result = await addMemberTCARemsScores({
        member_id: memberId,
        remsScore: 0, //This is no longer used, but we're keeping it in case the client wants it back some day
        remsSubscaleATD: tcaInitialSteps.remsSubscaleATD,
        remsSubscaleSM: tcaInitialSteps.remsSubscaleSM,
        remsSubscaleSS: tcaInitialSteps.remsSubscaleSS,
        setLoadingState: setLoadingState,
      });
      if (result["success"] === true) {
        resolve(`remsPromise succeeded with result is ${JSON.stringify(result)}`);
      } else {
        reject(`remsPromise failed with result is ${JSON.stringify(result)}`);
      }
    });
    await remsPromise;

    if (initialAdd) {
      const deletePromise = new Promise(async function (resolve, reject) {
        const result = await deleteTCAMemberAppointments({
          full_delete: true,
          member_id: memberId,
          setLoadingState: setLoadingState,
        });
        if (result["success"] === true) {
          resolve(`deletePromise succeeded with result is ${JSON.stringify(result)}`);
        } else {
          reject(`deletePromise failed with result is ${JSON.stringify(result)}`);
        }
      });
      await deletePromise;
    }
    //Add PGPs
    const pgpPromise = new Promise(async function (resolve, reject) {
      const result = await addMemberTCAGoals({
        longTermGoals: tcaInitialSteps.pgp,
        member_id: memberId,
        nextAppointmentDate: tcaInitialSteps.nextAppointmentDate,
        setLoadingState: setLoadingState,
      });
      if (result["success"] === true) {
        resolve(`pgpPromise succeeded with result is ${JSON.stringify(result)}`);
      } else {
        reject(`pgpPromise failed with result is ${JSON.stringify(result)}`);
      }
    });
    await pgpPromise;

    //Add PFI
    const pfipromise = new Promise(async function (resolve, reject) {
      const result = await addMemberTCAPersonalFeelingsInventory({
        energy_score: tcaInitialSteps.energy_score ?? 0,
        fatigue_score: tcaInitialSteps.fatigue_score ?? 0,
        member_id: memberId,
        setLoadingState: setLoadingState,
        stress_score: tcaInitialSteps.stress_score ?? 0,
      });
      if (result["success"] === true) {
        resolve(`pfipromise succeeded with result is ${JSON.stringify(result)}`);
      } else {
        reject(`pfipromise failed with result is ${JSON.stringify(result)}`);
      }
    });
    await pfipromise;

    setLoading(false);

    if (goToWorkoutPage) {
      navigate(`/members/${memberId}?addWorkoutPlan=true`);
    } else {
      onClose(true);
    }
  };
  //#endregion event handlers

  return (
    <>
      <Modal
        closeAfterTransition
        BackdropProps={{
          timeout: 500,
        }}
        open={open}
        onClose={handleCloseModal}
      >
        <Stack className="personal-details-modal-container">
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            marginBottom="40px"
            className="header"
            position={"relative"}
          >
            <Box flexGrow={1}>
              {workflowState !== "tcainit" && <h1>The Coach Approach &reg;</h1>}
              {workflowState === "tcainit" && <IconYMCATCALogo />}
            </Box>
            <Box className="tcaWorkflowSteps">
              <Box
                className={`tcaWorkflowStep ${
                  workflowState === "readiness" ? "active" : "complete"
                }`}
                onClick={() => handleSwitchMainWorkflowTab("readiness")}
              >
                <span>
                  01
                  <IconWorkflowStepActive className="active" />
                  <IconWorkflowStepComplete className="complete" />
                  <IconWorkflowStepLocked className="locked" />
                </span>
                <span>Coach Approach Readiness</span>
              </Box>
              {!existingMember && (
                <Box
                  className={`tcaWorkflowStep ${
                    workflowState === "readiness"
                      ? "locked"
                      : workflowState === "registration"
                      ? "active"
                      : "complete"
                  }`}
                  onClick={() => handleSwitchMainWorkflowTab("registration")}
                >
                  <span>
                    02
                    <IconWorkflowStepActive className="active" />
                    <IconWorkflowStepComplete className="complete" />
                    <IconWorkflowStepLocked className="locked" />
                  </span>
                  <span>Member Registration</span>
                </Box>
              )}
              <Box
                className={`tcaWorkflowStep ${workflowState !== "tcainit" ? "locked" : "active"}`}
                onClick={() => handleSwitchMainWorkflowTab("tcainit")}
              >
                <span>
                  {!existingMember ? `03` : `02`}
                  <IconWorkflowStepActive className="active" />
                  <IconWorkflowStepComplete className="complete" />
                  <IconWorkflowStepLocked className="locked" />
                </span>
                <span>TCA Steps</span>
              </Box>
            </Box>
            <DeleteIcon width="24px" height="24px" onClick={handleCloseModal} />
          </Stack>
          {loading && <ScreenLoader isScreenLoader />}
          <Box className={loading && `isHidden`} flexGrow={1}>
            {workflowState === "readiness" && (
              <Stack height={"100%"}>
                <h2>Coach Approach Readiness</h2>
                <Box mt={"20px"} mb={"10px"} sx={{ fontSize: "16px", fontWeight: "bold" }}>
                  The participant questions below are required for all members. Capture fitness and
                  health information using the options below.
                </Box>
                <Box flexGrow={1}>
                  <Box className="rowQuestionBox">
                    <Stack direction={"row"} alignItems="center">
                      <Box flexGrow={1}>
                        Does the member exercise regularly, 2 - 3 times every week?
                      </Box>
                      <Box width={"130px"}>
                        <ButtonGroup className="button-group">
                          <MuiButton
                            className={`${!readinessFirstQuestionAnswerIsYes ? `active` : ``}`}
                            onClick={() => setReadinessFirstQuestionAnswerIsYes(false)}
                          >
                            No
                          </MuiButton>
                          <MuiButton
                            className={`${readinessFirstQuestionAnswerIsYes ? `active green` : ``}`}
                            onClick={() => setReadinessFirstQuestionAnswerIsYes(true)}
                          >
                            Yes
                          </MuiButton>
                        </ButtonGroup>
                      </Box>
                    </Stack>
                  </Box>
                  <Box className="rowQuestionBox">
                    <Stack direction={"row"} alignItems="center">
                      <Box flexGrow={1}>
                        Has the member been doing so consistently for the last 6 months or longer?
                      </Box>
                      <Box width={"130px"}>
                        <ButtonGroup className="button-group">
                          <MuiButton
                            className={`${!readinessSecondQuestionAnswerIsYes ? `active` : ``}`}
                            onClick={() => setReadinessSecondQuestionAnswerIsYes(false)}
                          >
                            No
                          </MuiButton>
                          <MuiButton
                            className={`${
                              readinessSecondQuestionAnswerIsYes ? `active green` : ``
                            }`}
                            onClick={() => setReadinessSecondQuestionAnswerIsYes(true)}
                          >
                            Yes
                          </MuiButton>
                        </ButtonGroup>
                      </Box>
                    </Stack>
                  </Box>
                </Box>
                {readinessFirstQuestionAnswerIsYes && readinessSecondQuestionAnswerIsYes && (
                  <Box className="errorBox" sx={{ fontSize: "18px", fontWeight: "bold" }}>
                    <IconUserIneligible />
                    <Box sx={{ my: 2 }}>
                      This member is not technically qualified for THE COACH APPROACH Program.
                      However, you may continue to add them at your discretion.
                      <p>
                        Use the button below to continue adding the member to THE COACH APPROACH.
                      </p>
                      Or,
                      <p>
                        <a href="/members">Click here</a> to redirect from THE COACH APPROACH and
                        add member in FitLinxx.
                      </p>
                    </Box>
                  </Box>
                )}
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  marginTop="30px"
                >
                  {readinessFirstQuestionAnswerIsYes && readinessSecondQuestionAnswerIsYes && (
                    <Button
                      label="Add to THE COACH APPROACH"
                      // className="confirm-button"
                      onClick={() => {
                        if (existingMember) {
                          setWorkflowState("tcainit");
                        } else {
                          setWorkflowState("registration");
                        }
                      }}
                    />
                  )}
                  <Stack direction="row" alignItems="center" justifyContent="flex-end" flexGrow={1}>
                    <Stack direction="row" alignItems="center" spacing={3}>
                      <Button label="Cancel" className="text-button" onClick={handleCloseModal} />
                      {readinessFirstQuestionAnswerIsYes && readinessSecondQuestionAnswerIsYes && (
                        <Button
                          label="Close"
                          className="confirm-button"
                          onClick={handleCloseModal}
                        />
                      )}
                      {(!readinessFirstQuestionAnswerIsYes ||
                        !readinessSecondQuestionAnswerIsYes) && (
                        <Button
                          label="Next"
                          className="confirm-button"
                          onClick={() => {
                            if (existingMember) {
                              setWorkflowState("tcainit");
                            } else {
                              setWorkflowState("registration");
                            }
                          }}
                        />
                      )}
                    </Stack>
                  </Stack>
                </Stack>
              </Stack>
            )}
            {/* Get the IntialTCASteps/index.tsx form and puts it here */}
            <MemberForm
              onClose={handleCloseModal}
              open={workflowState === "registration"}
              sendMemberBackCallback={handleMemberObjectUpdated}
              ref={memberFormRef}
              takeMeBack={() => setWorkflowState("readiness")}
            />
            {workflowState === "tcainit" && (
              <InitialTCASteps
                onClose={handleCloseModal}
                open={open}
                member={memberToRegister || existingMember}
                sendTCABackCallback={handleTCAObjectUpdated}
                takeMeBack={() => {
                  if (existingMember) {
                    setWorkflowState("readiness");
                  } else {
                    setWorkflowState("registration");
                  }
                }}
              />
            )}
          </Box>
        </Stack>
      </Modal>
      <ConfirmationBox
        open={showDataWillBeLostModal}
        onClose={handleCloseModal}
        title={"Discard Changes?"}
        message={<>If answered Yes, all information filled will be lost.</>}
        type={"confirmation"}
      />
    </>
  );
};

export default AddMemberToCoachApproachModal;
